import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot
} from '@angular/router';
import { Observable } from 'rxjs';
import { LocalStorageService, SessionStorageService } from 'ngx-webstorage';

import CONFIGURATION from '../../configurations/configuration';
import { ENVIRONMENT } from '../../environments/environment';

import { EventEmitter } from '../models/generic.class';

import { AuthService } from './auth.service';
import { FeatureFlagService } from '../services/feature-flag.service';
import { FinanceService } from './finance.service';
import { MemberService } from './member.service';
import { NotificationService } from './notification.service';
import { SecurityService } from './security.service';
import { UserService } from './user.service';
import { SsoService } from './sso.service';
import { UtilityService } from './utility.service';

declare let CryptoJS, HmacSHA256: Function;
import * as Sentry from '@sentry/angular';
@Injectable()
export class AuthGuard implements CanActivate {
  private _borrowerRoutesDictionary: any;
  private _borrowerRoutes: Array<string>;
  private _investorRoutesDictionary: any;
  private _investorRoutes: Array<string>;
  private _masterData: any;
  navigationEventEmitter: EventEmitter;
  firstMinumumDeposit: number;
  private _securedBorrowerRoutes: Array<string>;
  private _securedInvestorRoutes: Array<string>;
  featureFlagObservable: Observable<any>;
  showIntercom: boolean;
  authDetails: any;
  isSubAccount: boolean;
  memberTypeCodes = CONFIGURATION.member_type_code;
  countryCode = CONFIGURATION.country_code;

  constructor(
    private authService: AuthService,
    private localStorageService: LocalStorageService,
    private financeService: FinanceService,
    private featureFlagService: FeatureFlagService,
    private memberService: MemberService,
    private notificationService: NotificationService,
    private router: Router,
    private securityService: SecurityService,
    private userService: UserService,
    private ssoService: SsoService,
    private sessionStorageService: SessionStorageService,
    private utilityService: UtilityService,
  ) {
    this._borrowerRoutesDictionary = {
      sign_up: '/admin-borrower/sign-up',
      activity: '/admin-borrower/activity',
      homepage: '/admin-borrower',
      new_loan: '/admin-borrower/new-loan',
      overview: '/admin-borrower/overview',
      referral: '/admin-borrower/referral',
      repayment: '/admin-borrower/repayment',
      setting: '/admin-borrower/setting',
      partner: '/admin-borrower/partner',
      statement: '/admin-borrower/statement',
      facility: '/admin-borrower/facility',
    };
    this._borrowerRoutes = new Array<string>();
    this._borrowerRoutes.push(this._borrowerRoutesDictionary.sign_up);
    this._borrowerRoutes.push(this._borrowerRoutesDictionary.homepage);
    this._borrowerRoutes.push(this._borrowerRoutesDictionary.activity);
    this._borrowerRoutes.push(this._borrowerRoutesDictionary.new_loan);
    this._borrowerRoutes.push(this._borrowerRoutesDictionary.overview);
    this._borrowerRoutes.push(this._borrowerRoutesDictionary.referral);
    this._borrowerRoutes.push(this._borrowerRoutesDictionary.repayment);
    this._borrowerRoutes.push(this._borrowerRoutesDictionary.setting);
    this._borrowerRoutes.push(this._borrowerRoutesDictionary.partner);
    this._borrowerRoutes.push(this._borrowerRoutesDictionary.statement);
    this._borrowerRoutes.push(this._borrowerRoutesDictionary.facility);

    this._investorRoutesDictionary = {
      activity: '/admin-investor/activity',
      browse_loan: '/admin-investor/browse-loan',
      deposit: '/admin-investor/deposit',
      homepage: '/admin-investor',
      investor_knowledge_testing: '/admin-investor/investor-knowledge-testing',
      overview: '/admin-investor/overview',
      payment: '/admin-investor/payment',
      portfolio: '/admin-investor/portfolio',
      referral: '/admin-investor/referral',
      sbn_retail: '/admin-investor/sbn-retail',
      setting: '/admin-investor/setting',
      sign_up: '/admin-investor/sign-up',
      setting_password: '/admin-investor/setting/change-password',
      setting_bank: '/admin-investor/setting/bank',
      setting_auto: '/admin-investor/setting/auto-investment',
      setting_priority_investment: '/admin-investor/setting/priority-investment',
      setting_accredited_declaration: '/admin-investor/setting/accredited-declaration',
      setting_personal: '/admin-investor/setting/personal',
      setting_subscription_agreement: '/admin-investor/setting/subscription-agreement',
      setting_user_agreement: '/admin-investor/setting/user-agreement',
      statement: '/admin-investor/statement',
      subscription_agreement: '/admin-investor/subscription-agreement',
      rdn_activation: '/admin-investor/rdn-activation',
      withdrawal: '/admin-investor/withdrawal'
    };
    this._investorRoutes = new Array<string>();
    this._investorRoutes.push(this._investorRoutesDictionary.homepage);
    this._investorRoutes.push(this._investorRoutesDictionary.activity);
    this._investorRoutes.push(this._investorRoutesDictionary.browse_loan);
    this._investorRoutes.push(this._investorRoutesDictionary.deposit);
    this._investorRoutes.push(this._investorRoutesDictionary.deposit_fpx);
    this._investorRoutes.push(this._investorRoutesDictionary.overview);
    this._investorRoutes.push(this._investorRoutesDictionary.payment);
    this._investorRoutes.push(this._investorRoutesDictionary.portfolio);
    this._investorRoutes.push(this._investorRoutesDictionary.referral);
    this._investorRoutes.push(this._investorRoutesDictionary.sbn_retail);
    this._investorRoutes.push(this._investorRoutesDictionary.setting);
    this._investorRoutes.push(this._investorRoutesDictionary.sign_up);
    this._investorRoutes.push(this._investorRoutesDictionary.setting_bank);
    this._investorRoutes.push(this._investorRoutesDictionary.setting_personal);
    this._investorRoutes.push(this._investorRoutesDictionary.setting_password);
    this._investorRoutes.push(this._investorRoutesDictionary.setting_auto);
    this._investorRoutes.push(this._investorRoutesDictionary.setting_priority_investment);
    this._investorRoutes.push(this._investorRoutesDictionary.setting_accredited_declaration);
    this._investorRoutes.push(this._investorRoutesDictionary.setting_subscription_agreement);
    this._investorRoutes.push(this._investorRoutesDictionary.setting_user_agreement);
    this._investorRoutes.push(this._investorRoutesDictionary.statement);
    this._investorRoutes.push(this._investorRoutesDictionary.subscription_agreement);
    this._investorRoutes.push(this._investorRoutesDictionary.rdn_activation);
    this._investorRoutes.push(this._investorRoutesDictionary.withdrawal);
    this._investorRoutes.push(this._investorRoutesDictionary.investor_knowledge_testing);

    this._masterData = {
      activationStepCodes: CONFIGURATION.activation_step_code,
      lastSignUpWebStatus: CONFIGURATION.last_sign_up_web_status,
      memberStatusCode: CONFIGURATION.member_status.reverification,
      memberTypeCodes: CONFIGURATION.member_type_code
    };
    this.firstMinumumDeposit = 0;
    this.navigationEventEmitter = new EventEmitter();
    this._securedBorrowerRoutes = new Array<string>();
    this._securedInvestorRoutes = new Array<string>();
    this.memberService.getCountryDetail(CONFIGURATION.country_code).subscribe(data => {
      if (
        data && data.deposit_settings &&
        data.deposit_settings.minimum_first_deposit
      ) {
        this.firstMinumumDeposit = data.deposit_settings.minimum_first_deposit;
      }
    });
    this.showIntercom = false;
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
    let url: string = state.url;
    const { intercomFlag } = this.featureFlagService.getFeatureFlagKeys();
    this.featureFlagObservable = this.featureFlagService.getFeatureFlagObservable();
    this.featureFlagObservable.subscribe((flags) => {
      this.showIntercom = flags[intercomFlag];
    });
    if (CONFIGURATION.intercom.enable && this.showIntercom) {
      window['Intercom']('shutdown');
      const intercomConfiguration = ENVIRONMENT.intercom[(CONFIGURATION.country_code).toLowerCase()];
      const hash = CryptoJS.HmacSHA256(this.authService.getMemberId().toString(), intercomConfiguration.hashCode);
      const hashInHex = CryptoJS.enc.Hex.stringify(hash);
      window['Intercom']('boot', {
        app_id: intercomConfiguration.appId,
        email: this.authService.getUserName().toString(),
        user_id: this.authService.getMemberId().toString(),
        user_hash: hashInHex
      });
    }

    this._resetSecuredRoutes();
    const urlParams = new URLSearchParams(window.location.search);
    const redirect = urlParams.get('redirect');
    if (redirect) {
      const redirectUriKey = this.securityService.encode(CONFIGURATION.encryption_key, "redirect_uri");
      this.localStorageService.store(redirectUriKey, this.securityService.encode(CONFIGURATION.encryption_key, redirect));
      const redirectUri = this.getRedirectUrl(redirect);
      url = redirectUri;
      if (redirectUri) {
        url = redirectUri;
      }
    }
    return this.authenticate()
      .then(() => {
        return this.routeValidation(url);
      })
      .catch(() => {
        // to allow sign up user to bypass fusionauth authentication
        const userIsNotActivated =
          this.authService.getActivationStepCode() !== this._masterData.activationStepCodes['activated'];
        if (userIsNotActivated) {
          return this.routeValidation(url);
        }
        this.ssoService.redirectToLogoutPage();
        return false;
      });
  }

  fallbackToLoginPage(url: string): boolean {
    let redirectUri = '';
    if (url.includes('admin-investor') && CONFIGURATION.navigation.loginDropDown) {
      redirectUri = '/log-in/investor';
    } else if (url.includes('admin-borrower ') && CONFIGURATION.navigation.loginDropDown) {
      redirectUri = '/log-in/borrower';
    } else {
      redirectUri = '/log-in';
    }
    if (this.authService.getBearerToken() == '') {
      this.ssoService.redirectToLogoutPage();
    }
    this.router.navigate([redirectUri], {
      queryParams: {
        redirect: this.securityService.encode(CONFIGURATION.encryption_key, url)
      }
    });
    return false;
  }

  routeValidation(url: string): boolean {
    const sanitizedUrl = url.split('?')[0];
    if (this.authService.isLoggedIn()) {
      switch (this.authService.getMemberTypeCode()) {
        case this._masterData.memberTypeCodes['investor']:
          // Fallback to `Getting Started` if route is invalid.
          if (!this._investorRoutes.find(element => { return element === sanitizedUrl })) {
            setTimeout(() => {
              this.router.navigate([this._investorRoutesDictionary.homepage]);
            }, 0);
          }

          // Fallback to respective page if activation is not completed and route is restricted.
          if (
            !this._securedInvestorRoutes.find(element => { return element === sanitizedUrl }) &&
            this.authService.getActivationStepCode() !== this._masterData.activationStepCodes['activated']
          ) {
            switch (CONFIGURATION.country_code) {
              case 'ID':
                switch (this.authService.getActivationStepCode()) {
                  case '':
                  case this._masterData.activationStepCodes['null']:
                    setTimeout(() => {
                      this.notificationService.info(this.localStorageService.retrieve('unauthorized-message'));
                      if (this._masterData.memberStatusCode === this.authService.getMemberStatusCode()) {
                        this.router.navigate([this._investorRoutesDictionary.subscription_agreement]);
                      } else {
                        this.router.navigate([this._investorRoutesDictionary.sign_up]);
                      }
                    }, 0);
                    break;
                  case this._masterData.activationStepCodes['fill_information']:
                  case this._masterData.activationStepCodes['first_deposit']:
                    setTimeout(() => {
                      // Temporary remove -> Search for first-deposit-message to enable again
                      // this.notificationService.info(this.localStorageService.retrieve('first-deposit-message'));
                      this.router.navigate([this._investorRoutesDictionary.deposit]);
                    }, 0);
                    break;
                  case this._masterData.activationStepCodes['deposit']:
                    setTimeout(() => {
                      this.financeService
                        .getBalance()
                        .subscribe(
                          balance => {
                            if (balance.value >= this.firstMinumumDeposit) {
                              this.notificationService.info(this.localStorageService.retrieve('activation-message'));
                              this.router.navigate([this._investorRoutesDictionary.homepage]);
                            } else {
                              // Temporary remove -> Search for first-deposit-message to enable again
                              // this.notificationService.info(this.localStorageService.retrieve('first-deposit-message'));
                              this.router.navigate([this._investorRoutesDictionary.deposit]);
                            }
                          },
                          error => {
                            console.error('ERROR', error);
                          }
                        );
                    }, 0);
                    break;
                  case this._masterData.activationStepCodes['send_econtract']:
                  case this._masterData.activationStepCodes['sign_econtract']:
                    setTimeout(() => {
                      this.router.navigate([this._investorRoutesDictionary.homepage]);
                    }, 0);
                    break;
                  default:
                    setTimeout(() => {
                      this.router.navigate([this._investorRoutesDictionary.overview]);
                    }, 0);
                    break;
                }
                break;
              case 'MY':
                switch (this.authService.getActivationStepCode()) {
                  case '':
                  case this._masterData.activationStepCodes['null']:
                    setTimeout(() => {
                      this.notificationService.info(this.localStorageService.retrieve('unauthorized-message'));
                      this.router.navigate([this._investorRoutesDictionary.sign_up]);
                    }, 0);
                    break;
                  case this._masterData.activationStepCodes['fill_information']:
                    setTimeout(() => {
                      this.notificationService.info(this.localStorageService.retrieve('unauthorized-message'));
                      this.router.navigate([this._investorRoutesDictionary.deposit]);
                    }, 0);
                    break;
                  case this._masterData.activationStepCodes['first_deposit']:
                  case this._masterData.activationStepCodes['deposit']:
                  case this._masterData.activationStepCodes['send_econtract']:
                  case this._masterData.activationStepCodes['sign_econtract']:
                  case this._masterData.activationStepCodes['escrow_agent']:
                    setTimeout(() => {
                      this.notificationService.info(this.localStorageService.retrieve('unauthorized-message'));
                      this.router.navigate([this._investorRoutesDictionary.homepage]);
                    }, 0);
                    break;
                  default:
                    setTimeout(() => {
                      this.router.navigate([this._investorRoutesDictionary.overview]);
                    }, 0);
                    break;
                }
                break;
              case 'SG':
                switch (this.authService.getActivationStepCode()) {
                  case this._masterData.activationStepCodes['null']:
                    setTimeout(() => {
                      this.notificationService.info(this.localStorageService
                        .retrieve('unauthorized-message'));
                      this.router.navigate(
                        [this._investorRoutesDictionary.sign_up],
                        { queryParamsHandling: 'merge' },
                      );
                    }, 0);
                    break;
                  case '':
                  case this._masterData.activationStepCodes['generic_questionnaire']:
                  case this._masterData.activationStepCodes['suitability_assessment_test']:
                    setTimeout(() => {
                      this.notificationService.info(this.localStorageService.retrieve('unauthorized-message'));
                      this.router.navigate([this._investorRoutesDictionary.subscription_agreement]);
                    }, 0);
                    break;
                  case this._masterData.activationStepCodes['fill_information']:
                  case this._masterData.activationStepCodes['send_econtract']:
                  case this._masterData.activationStepCodes['sign_econtract']:
                    setTimeout(() => {
                      this.notificationService.info(this.localStorageService.retrieve('unauthorized-message'));
                      this.router.navigate([this._investorRoutesDictionary.homepage]);
                    }, 0);
                    break;
                  case this._masterData.activationStepCodes['escrow_agent']:
                    setTimeout(() => {
                      this.notificationService.info(this.localStorageService.retrieve('unauthorized-message'));
                      this.router.navigate([this._investorRoutesDictionary.deposit]);
                    }, 0);
                    break;
                  case this._masterData.activationStepCodes['first_deposit']:
                  case this._masterData.activationStepCodes['deposit']:
                  default:
                    setTimeout(() => {
                      this.router.navigate([this._investorRoutesDictionary.overview]);
                    }, 0);
                    break;
                }
                break;
              case 'TH':
                switch (this.authService.getActivationStepCode()) {
                  case this._masterData.activationStepCodes['null']:
                    setTimeout(() => {
                      this.notificationService.info(this.localStorageService
                        .retrieve('unauthorized-message'));
                      this.router.navigate(
                        [this._investorRoutesDictionary.sign_up],
                        { queryParamsHandling: 'merge' },
                      );
                    }, 0);
                    break;
                  case '':
                  case this._masterData.activationStepCodes['generic_questionnaire']:
                  case this._masterData.activationStepCodes['suitability_assessment_test']:
                    setTimeout(() => {
                      this.notificationService.info(this.localStorageService.retrieve('unauthorized-message'));
                      this.router.navigate([this._investorRoutesDictionary.subscription_agreement]);
                    }, 0);
                    break;
                  case this._masterData.activationStepCodes['fill_information']:
                  case this._masterData.activationStepCodes['investor_knowledge_test']:
                    setTimeout(() => {
                      this.notificationService.info(this.localStorageService.retrieve('unauthorized-message'));
                      this.router.navigate([this._investorRoutesDictionary.homepage]);
                    }, 0);
                    break;
                  default:
                    setTimeout(() => {
                      this.router.navigate([this._investorRoutesDictionary.overview]);
                    }, 0);
                    break;
                }
                break;
            }
          }
          break;
        case this._masterData.memberTypeCodes['borrower']:
          // Fallback to `Overview` if route is invalid.
          if (!this._borrowerRoutes.find(element => { return element === sanitizedUrl })) {
            setTimeout(() => {
              this.router.navigate([this._borrowerRoutesDictionary.overview]);
            }, 0);
          }
          this.memberService.getMemberDetail().subscribe(
            member => {
              if (member && member.data) {
                const memberData = member.data;
                if (memberData.last_sign_up_web_status) {
                  switch (CONFIGURATION.country_code) {
                    case 'MY':
                      switch (memberData.last_sign_up_web_status) {
                        case this._masterData.lastSignUpWebStatus['accountVerification']:
                        case this._masterData.lastSignUpWebStatus['borrowerPersonalInformation']:
                        case this._masterData.lastSignUpWebStatus['borrowerBusinessInformation']:
                        case this._masterData.lastSignUpWebStatus['borrowerFinancialInformation']:
                          setTimeout(() => {
                            this.router.navigate(['/admin-borrower/sign-up']);
                          }, 0);
                          break;
                      }
                      break;
                    case 'SG':
                      if (memberData.last_sign_up_web_status !== this._masterData.lastSignUpWebStatus['borrowerBusinessInformation']) {
                        switch (memberData.last_sign_up_web_status) {
                          case '':
                          case this._masterData.lastSignUpWebStatus['accountVerification']:
                            setTimeout(() => {
                              this.router.navigate(['/admin-borrower/sign-up']);
                            }, 0);
                            break;
                        }
                      }
                      break;
                    case 'TH':
                      if (memberData.last_sign_up_web_status !== this._masterData.lastSignUpWebStatus['borrowerBusinessInformation']) {
                        switch (memberData.last_sign_up_web_status) {
                          case '':
                          case this._masterData.lastSignUpWebStatus['accountVerification']:
                            setTimeout(() => {
                              this.router.navigate(['/admin-borrower/sign-up']);
                            }, 0);
                            break;
                        }
                      }
                      break;
                  }
                  if (!this._borrowerRoutes.find(element => { return element === sanitizedUrl })) {
                    setTimeout(() => {
                      this.router.navigate([this._borrowerRoutesDictionary.homepage]);
                    }, 0);
                  }
                } else {
                  if (!memberData.company_name) {
                    setTimeout(() => {
                      this.router.navigate(['/admin-borrower/sign-up']);
                    }, 0);
                  }
                  const body = {
                    lastSignUpStepWeb: this.authService.getlastSignUpStepWeb()
                  };
                  if (body.lastSignUpStepWeb) {
                    this.memberService.updateMember(body).subscribe(
                      response => {
                        this.authService.setLastSignUStepWeb(this.authService.getlastSignUpStepWeb());
                      },
                      error => {
                        this.notificationService.error();
                      });
                  }
                }
              }
            },
            error => {
              this.notificationService.error();
            }
          );
      }
      return true;
    }
    setTimeout(() => {
      this.fallbackToLoginPage(url);
    }, 0);
  }

  authenticate() {
    return new Promise<void>((resolve, reject) => {
      const auth = JSON.parse(this.localStorageService.retrieve('auth'));
      this.authDetails = { auth };

      if (auth) {
        resolve();
      } else {
        const urlParams = new URLSearchParams(window.location.search);
        const authCode = urlParams.get('code');
        const receivedState = urlParams.get('state');
        const authState = urlParams.get('userState');
        const storedState = this.localStorageService.retrieve('state');
        const allowedUserState = [
          'Authenticated',
          'AuthenticatedRegistrationNotVerified',
        ];

        if (authCode && allowedUserState.includes(authState) && storedState === receivedState) {
          this.ssoService
            .exchangeCodeForTokens(authCode)
            .then(auth => {
              this.localStorageService.clear('codeVerifier');
              history.replaceState(null, '', window.location.pathname);
              this.authDetails.auth = auth;
              const expiryTime = new Date();
              expiryTime.setTime(expiryTime.getTime() + (auth.expires_in * 1000));
              this.authService.setBearerToken(auth.access_token);
              this.authService.setExpiryTime(expiryTime);
              this.authService.setRelativeExpiryTime(auth.expires_in);
              this.authService.setRefreshToken(auth.refresh_token);

              this.memberService
                .getLoginDetail()
                .subscribe(
                  member => {
                    this.authService.logIn(
                      member.memberTypeName,
                      String(member.id),
                      member.userName,
                      member.uuid
                    );
                    this.featureFlagService.initializeLdClient();
                    Sentry.setContext('character', {
                      email: member.userName
                    });
                    window['FS'].identify(member.id, {
                      email: member.userName
                    });

                    if (
                      member &&
                      member.countryId === CONFIGURATION.country_code &&
                      member.memberTypeCode === this.authService.getMemberTypeCode()
                    ) {
                      // ACCESS LEVEL VALIDATION
                      let accessLevel = CONFIGURATION.access_level_code.administrator;
                      let subaccount = null;
                      // ACTIVATION STEP VALIDATION
                      let activationStepCode = '';
                      if (
                        member.activationStepCode !== null &&
                        member.activationStepCode === CONFIGURATION.activation_step_code.fill_information
                      ) {
                        this.financeService.getDeposit()
                          .subscribe(
                            response => {
                              if (CONFIGURATION.wallet.show) {
                                activationStepCode = response.value.total > 0 ?
                                  CONFIGURATION.activation_step_code.first_deposit :
                                  (
                                    member.activationStepCode !== null ?
                                      member.activationStepCode :
                                      CONFIGURATION.activation_step_code.null
                                  );
                              } else {
                                activationStepCode = member.activationStepCode;
                              }
                              this.setLoginDetails(accessLevel, activationStepCode, member, subaccount);
                            },
                            error => {
                              activationStepCode = member.activationStepCode !== null ?
                                member.activationStepCode :
                                CONFIGURATION.activation_step_code.null;
                              this.setLoginDetails(accessLevel, activationStepCode, member, subaccount);
                            }
                          );
                      } else {
                        activationStepCode = member.activationStepCode !== null ?
                          member.activationStepCode :
                          CONFIGURATION.activation_step_code.null;
                        this.setLoginDetails(accessLevel, activationStepCode, member, subaccount);
                      }
                    } else {
                      this.ssoService.redirectToLogoutPage();
                    }
                    resolve();
                  },
                  error => {
                    const expiryTime = this.authService.getExpiryTime();
                    const sessionAbleToRefresh = expiryTime !== '' && this.authService.getRememberMe();
                    const numberOfRefresh = this.sessionStorageService.retrieve('numberOfRefresh') ?
                      this.sessionStorageService.retrieve('numberOfRefresh') : 0;
                    if (sessionAbleToRefresh && numberOfRefresh < 3) {
                      this.sessionStorageService.store('numberOfRefresh', numberOfRefresh + 1);
                      this.ssoService.refreshJwtToken();
                    } else {
                      this.ssoService.redirectToLogoutPage();
                    }
                    reject();
                  }
                );

            })
            .catch(reject);
        } else {
          reject();
        }
      }
    });
  }

  setLoginDetails(accessLevel, activationStepCode, member, subaccount) {
    this.authService
      .setLogInDetails(
        accessLevel,
        activationStepCode,
        member.memberStatusCode,
        member.referralCode,
        member.referralId ? member.referralId : 0,
        subaccount ? subaccount.id : 0,
        subaccount ? subaccount.userName : '',
        member.isInstitutional,
        member.memberEntityTypeId,
        member.needsRDNOpening,
        member.activatedAt,
        member.memberChangeRequestEntity,
        member.memberEntityCode,
        member.memberActivationStatusCode,
      );

    switch (this.authService.getMemberTypeCode()) {
      case this.memberTypeCodes.borrower:
        this.router.navigate(['/admin-borrower/overview']);
        break;
      case this.memberTypeCodes.investor:
        this.router.navigate(['/admin-investor/overview']);
        break;
    }
  }

  public _isBorrowerRouteAccessible(route: string): boolean {
    const found = this._securedBorrowerRoutes.find(securedBorrowerRoute => {
      return securedBorrowerRoute === route;
    });
    return found ? true : false;
  }

  public _isInvestorRouteAccessible(route: string): boolean {
    const found = this._securedInvestorRoutes.find(securedInvestorRoute => {
      return securedInvestorRoute === route;
    });
    return found ? true : false;
  }

  getBorrowerNavigation(): any {
    const navigation = {
      showMenu: false,
      menu: {
        homepage: this._isBorrowerRouteAccessible(this._borrowerRoutesDictionary.homepage),
        new_loan: this._isBorrowerRouteAccessible(this._borrowerRoutesDictionary.new_loan),
        overview: this._isBorrowerRouteAccessible(this._borrowerRoutesDictionary.overview),
        referral: this._isBorrowerRouteAccessible(this._borrowerRoutesDictionary.referral),
        repayment: this._isBorrowerRouteAccessible(this._borrowerRoutesDictionary.repayment),
        partner: this._isBorrowerRouteAccessible(this._borrowerRoutesDictionary.partner),
        statement: this._isBorrowerRouteAccessible(this._borrowerRoutesDictionary.statement),
        facility: this._isBorrowerRouteAccessible(this._borrowerRoutesDictionary.facility),
      },
      subMenu: {
        activity: this._isBorrowerRouteAccessible(this._borrowerRoutesDictionary.activity),
        setting: this._isBorrowerRouteAccessible(this._borrowerRoutesDictionary.setting)
      }
    };
    for (const key in navigation.menu) {
      if (navigation.menu[key]) {
        navigation.showMenu = true;
      }
    }

    return navigation;
  }

  getInvestorNavigation(): any {
    const navigation = {
      showMenu: false,
      menu: {
        browse_loan: this._isInvestorRouteAccessible(this._investorRoutesDictionary.browse_loan),
        deposit: this._isInvestorRouteAccessible(this._investorRoutesDictionary.deposit),
        deposit_fpx: this._isInvestorRouteAccessible(this._investorRoutesDictionary.deposit_fpx),
        homepage: this._isInvestorRouteAccessible(this._investorRoutesDictionary.homepage),
        overview: this._isInvestorRouteAccessible(this._investorRoutesDictionary.overview),
        payment: this._isInvestorRouteAccessible(this._investorRoutesDictionary.payment),
        portfolio: this._isInvestorRouteAccessible(this._investorRoutesDictionary.portfolio),
        referral: this._isInvestorRouteAccessible(this._investorRoutesDictionary.referral),
        sbn_retail: this._isInvestorRouteAccessible(this._investorRoutesDictionary.sbn_retail),
        rdn_activation: this._isInvestorRouteAccessible(this._investorRoutesDictionary.rdn_activation),
        statement: this._isInvestorRouteAccessible(this._investorRoutesDictionary.statement),
        subscription_agreement: this._isInvestorRouteAccessible(this._investorRoutesDictionary.subscription_agreement),
        withdrawal: this._isInvestorRouteAccessible(this._investorRoutesDictionary.withdrawal)
      },
      setting: {
        auto_investment: this._isBorrowerRouteAccessible(this._borrowerRoutesDictionary.setting) && this.authService.isAdministrator(),
        bank: this._isBorrowerRouteAccessible(this._borrowerRoutesDictionary.setting) && this.authService.isAdministrator(),
        password: this._isBorrowerRouteAccessible(this._borrowerRoutesDictionary.setting) && this.authService.isAdministrator(),
        personal: this._isBorrowerRouteAccessible(this._borrowerRoutesDictionary.setting) && this.authService.isAdministrator(),
        subaccount_password: this._isBorrowerRouteAccessible(this._borrowerRoutesDictionary.setting) && !this.authService.isAdministrator(),
        suitability_assessment_test: this._isBorrowerRouteAccessible(this._borrowerRoutesDictionary.setting) && this.authService.isAdministrator() && CONFIGURATION.country_code === 'SG',
        subscription_agreement: this._isInvestorRouteAccessible(this._investorRoutesDictionary.setting)
          && this.authService.isAdministrator()
          && (CONFIGURATION.country_code === 'SG' || CONFIGURATION.country_code === 'MY')
      },
      subMenu: {
        activity: this._isInvestorRouteAccessible(this._investorRoutesDictionary.activity),
        setting: this._isInvestorRouteAccessible(this._investorRoutesDictionary.setting),
        setting_personal: this._isInvestorRouteAccessible(this._investorRoutesDictionary.setting_personal),
        setting_bank: this._isInvestorRouteAccessible(this._investorRoutesDictionary.setting_bank),
        setting_password: this._isInvestorRouteAccessible(this._investorRoutesDictionary.setting_password) && this.authService.isAdministrator(),
        setting_auto: this._isInvestorRouteAccessible(this._investorRoutesDictionary.setting_auto),
        setting_priority_investment:
          this._isInvestorRouteAccessible(this._investorRoutesDictionary.setting_priority_investment),
        setting_accredited_declaration: this._isInvestorRouteAccessible(this._investorRoutesDictionary.setting_accredited_declaration),
        setting_subscription_agreement: this._isInvestorRouteAccessible(this._investorRoutesDictionary.setting_subscription_agreement),
      }
    };
    for (const key in navigation.menu) {
      if (navigation.menu[key]) {
        navigation.showMenu = true;
      }
    }

    return navigation;
  }

  private _resetSecuredRoutes(): void {
    this._securedBorrowerRoutes = new Array<string>();
    this._securedBorrowerRoutes.push(this._borrowerRoutesDictionary.homepage);
    this._securedBorrowerRoutes.push(this._borrowerRoutesDictionary.partner);
    this._securedBorrowerRoutes.push(this._borrowerRoutesDictionary.activity);
    this._securedBorrowerRoutes.push(this._borrowerRoutesDictionary.new_loan);
    this._securedBorrowerRoutes.push(this._borrowerRoutesDictionary.overview);
    this._securedBorrowerRoutes.push(this._borrowerRoutesDictionary.referral);
    this._securedBorrowerRoutes.push(this._borrowerRoutesDictionary.repayment);
    this._securedBorrowerRoutes.push(this._borrowerRoutesDictionary.setting);
    this._securedBorrowerRoutes.push(this._borrowerRoutesDictionary.statement);
    this._securedBorrowerRoutes.push(this._borrowerRoutesDictionary.facility);

    this._securedInvestorRoutes = new Array<string>();
    switch (CONFIGURATION.country_code) {
      case 'ID':
        switch (this.authService.getActivationStepCode()) {
          case '':
          case this._masterData.activationStepCodes['null']:
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.homepage);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.subscription_agreement);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.browse_loan);
            break;
          case this._masterData.activationStepCodes['fill_information']:
          case this._masterData.activationStepCodes['first_deposit']:
          case this._masterData.activationStepCodes['deposit']:
          case this._masterData.activationStepCodes['send_econtract']:
          case this._masterData.activationStepCodes['sign_econtract']:
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.homepage);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.deposit);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.browse_loan);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.sbn_retail);
            break;
          case this._masterData.activationStepCodes['activated']:
          default:
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.activity);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.overview);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.portfolio);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.referral);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.statement);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.browse_loan);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.deposit);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.deposit_fpx);
            if (this.authService.isAdministrator()) {
              this._securedInvestorRoutes.push(this._investorRoutesDictionary.rdn_activation);
              this._securedInvestorRoutes.push(this._investorRoutesDictionary.sbn_retail);
              this._securedInvestorRoutes.push(this._investorRoutesDictionary.withdrawal);
            }
            break;
        }
        break;
      case 'MY':
        switch (this.authService.getActivationStepCode()) {
          case '':
          case this._masterData.activationStepCodes['null']:
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.homepage);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.subscription_agreement);
            break;
          case this._masterData.activationStepCodes['fill_information']:
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.homepage);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.deposit);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_bank);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.browse_loan);
            break;
          case this._masterData.activationStepCodes['send_econtract']:
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.homepage);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.deposit);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_bank);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_personal);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_auto);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_priority_investment);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_password);
            break;
          case this._masterData.activationStepCodes['first_deposit']:
          case this._masterData.activationStepCodes['deposit']:
          case this._masterData.activationStepCodes['send_econtract']:
          case this._masterData.activationStepCodes['sign_econtract']:
          case this._masterData.activationStepCodes['escrow_agent']:
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.homepage);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.deposit);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.browse_loan);
            break;
          case this._masterData.activationStepCodes['activated']:
          default:
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.activity);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.overview);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.portfolio);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.referral);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_password);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_auto);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_priority_investment);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_bank);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_personal);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_subscription_agreement);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.rdn_activation);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.statement);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.browse_loan);
            if (this.authService.isAdministrator()) {
              this._securedInvestorRoutes.push(this._investorRoutesDictionary.deposit);
              this._securedInvestorRoutes.push(this._investorRoutesDictionary.deposit_fpx);
              this._securedInvestorRoutes.push(this._investorRoutesDictionary.withdrawal);
            }
            break;
        }
        break;
      case 'SG':
        switch (this.authService.getActivationStepCode()) {
          case '':
          case this._masterData.activationStepCodes['null']:
            break;
          case this._masterData.activationStepCodes['generic_questionnaire']:
          case this._masterData.activationStepCodes['suitability_assessment_test']:
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.homepage);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.subscription_agreement);
            break;
          case this._masterData.activationStepCodes['fill_information']:
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.homepage);
            break;
          case this._masterData.activationStepCodes['send_econtract']:
          case this._masterData.activationStepCodes['sign_econtract']:
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.homepage);
            break;
          case this._masterData.activationStepCodes['escrow_agent']:
          case this._masterData.activationStepCodes['first_deposit']:
          case this._masterData.activationStepCodes['deposit']:
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.homepage);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.deposit);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.browse_loan);
            break;
          case this._masterData.activationStepCodes['activated']:
          default:
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.activity);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.overview);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.portfolio);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.referral);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_password);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_personal);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_auto);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_accredited_declaration);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_bank);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_subscription_agreement);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.statement);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.browse_loan);
            if (this.authService.isAdministrator()) {
              this._securedInvestorRoutes.push(this._investorRoutesDictionary.deposit);
              this._securedInvestorRoutes.push(this._investorRoutesDictionary.deposit_fpx);
              this._securedInvestorRoutes.push(this._investorRoutesDictionary.withdrawal);
            }
            break;
        }
        break;
      case 'TH':
        switch (this.authService.getActivationStepCode()) {
          case '':
          case this._masterData.activationStepCodes['null']:
            break;
          case this._masterData.activationStepCodes['generic_questionnaire']:
          case this._masterData.activationStepCodes['suitability_assessment_test']:
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.homepage);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.subscription_agreement);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.investor_knowledge_testing);
            break;
          case this._masterData.activationStepCodes['fill_information']:
          case this._masterData.activationStepCodes['investor_knowledge_test']:
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.homepage);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.investor_knowledge_testing);
            break;
          case this._masterData.activationStepCodes['activated']:
          default:
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.activity);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.overview);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.payment);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.portfolio);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_password);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_personal);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_auto);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_accredited_declaration);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_bank);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.setting_subscription_agreement);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.statement);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.investor_knowledge_testing);
            this._securedInvestorRoutes.push(this._investorRoutesDictionary.browse_loan);
            break;
        }
        break;
    }

    this.navigationEventEmitter.emit(true);
  }

  getRedirectUrl(encryptedUrl: string) {
    return this.securityService.decode(CONFIGURATION.encryption_key, encryptedUrl);
  }

  getRoutesDictionary(memberTypeCode: string) {
    if (memberTypeCode === CONFIGURATION.member_type_code.borrower) {
      return this._borrowerRoutesDictionary;
    } else {
      return this._investorRoutesDictionary;
    }
  }

  getExcludedRedirectUrls(memberTypeCode) {
    const dictionary = this.getRoutesDictionary(memberTypeCode);
    return [
      dictionary.sign_up,
    ];
  }

  getValidRoute(memberTypeCode: string) {
    if (memberTypeCode === CONFIGURATION.member_type_code.borrower) {
      return this._borrowerRoutes;
    } else {
      return this._investorRoutes;
    }
  }
}
